import { oc } from "@orpc/contract";
import { z } from "zod";
import { TroubleDataSchema } from "@erp/shared-types";

/**
 * Sales Order RPC Contract
 *
 * Provides endpoints for fetching sales orders and their line items.
 */

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

const UUIDSchema = z.uuid();

/**
 * Sales order status enum
 */
export const SalesOrderStatusSchema = z.enum([
  "pending",
  "in_progress",
  "fulfilled",
  "closed",
]);

/**
 * Sales order sync status enum
 */
export const SalesOrderSyncStatusSchema = z.enum([
  "not_started",
  "pending",
  "in_progress",
  "success",
  "failed",
  "retrying",
]);

/**
 * Pagination schema
 */
export const SalesOrderPaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * Customer summary for sales order
 */
export const SalesOrderCustomerSummarySchema = z.object({
  id: UUIDSchema,
  name: z.string().nullable(),
  customerNumber: z.string().nullable().optional(),
});

/**
 * Shipment summary for sales order
 */
export const SalesOrderShipmentSummarySchema = z.object({
  id: UUIDSchema,
  originLoadNumber: z.string().nullable(),
  status: z.string().nullable(),
  origin: z.string().nullable(),
  destination: z.string().nullable(),
});

/**
 * Sales order line item schema
 */
export const SalesOrderLineItemSchema = z.object({
  id: UUIDSchema,
  lineItemNumber: z.int().nullable().optional(),
  quantity: z.number().nullable().optional(),
  unitPrice: DecimalStringSchema.nullable().optional(),
  lineTotal: DecimalStringSchema,
  costPrice: DecimalStringSchema.nullable().optional(),
  supplierCostPrice: DecimalStringSchema.nullable().optional(),
  supplierQuantity: z.number().nullable().optional(),
  isConsignment: z.boolean().nullable().optional(),
  productId: UUIDSchema.nullable().optional(),
  productVariantId: UUIDSchema.nullable().optional(),
  customerProductId: UUIDSchema.nullable().optional(),
  rescheduledDate: z.string().nullable().optional(),
  // Product details if available
  productName: z.string().nullable().optional(),
  productSku: z.string().nullable().optional(),
  productVariantSku: z.string().nullable().optional(),
  customerProductSku: z.string().nullable().optional(),
  troubleData: TroubleDataSchema,
});

/**
 * Customer/order context carried in `ar_sales_orders.origin_metadata`.
 *
 * For AS400/DMDTRANS-sourced orders there is no linked customer master record
 * (`customerId` is null), so these loose codes + name are the only customer
 * identity we have. Codes only — no contact/submitter info exists in DMDTRANS.
 * Loose so unrelated keys (e.g. troubleData) pass through without breaking.
 */
export const SalesOrderOriginMetadataSchema = z
  .object({
    soldToCode: z.string().nullable().optional(),
    shipToCode: z.string().nullable().optional(),
    customerName: z.string().nullable().optional(),
    channel: z.string().nullable().optional(),
    orderType: z.string().nullable().optional(),
    division: z.string().nullable().optional(),
  })
  .loose();

/**
 * Base sales order schema
 */
export const SalesOrderSchema = z.object({
  id: UUIDSchema,
  internalId: z.int(),
  organizationId: UUIDSchema,
  customerId: UUIDSchema.nullable(),
  shipmentId: UUIDSchema.nullable().optional(),
  source: z.string(),
  orderDate: z.string(),
  deliveryDate: z.string().nullable().optional(),
  shipDate: z.string().nullable().optional(),
  customerPurchaseOrderNumber: z.string().nullable().optional(),
  status: SalesOrderStatusSchema,
  totalAmount: DecimalStringSchema.nullable().optional(),
  currencyCode: z.string().nullable().optional(),
  originId: z.string().nullable().optional(),
  originSystem: z.string().nullable().optional(),
  originEntity: z.string().nullable().optional(),
  originSyncStatus: SalesOrderSyncStatusSchema.nullable().optional(),
  originMetadata: SalesOrderOriginMetadataSchema.nullable().optional(),
  notes: z
    .array(
      z.object({
        text: z.string(),
        createdAt: z.coerce.date(),
        userId: z.string(),
      }),
    )
    .nullable()
    .optional(),
  troubleData: TroubleDataSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * List sales orders input
 */
export const ListSalesOrdersInputSchema = z.object({
  customerId: UUIDSchema.optional(),
  shipmentId: UUIDSchema.optional(),
  status: z.array(SalesOrderStatusSchema).optional(),
  orderDateFrom: z.string().optional(),
  orderDateTo: z.string().optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * List sales orders response
 */
export const ListSalesOrdersResponseSchema = z.object({
  salesOrders: z.array(
    SalesOrderSchema.extend({
      customer: SalesOrderCustomerSummarySchema.nullable().optional(),
    }),
  ),
  pagination: SalesOrderPaginationSchema,
});

/**
 * Get sales order input
 */
export const GetSalesOrderInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Get sales order response
 */
export const GetSalesOrderResponseSchema = z.object({
  salesOrder: SalesOrderSchema.extend({
    customer: SalesOrderCustomerSummarySchema.nullable().optional(),
    shipment: SalesOrderShipmentSummarySchema.nullable().optional(),
  }).nullable(),
  lineItems: z.array(SalesOrderLineItemSchema),
});

/**
 * Oracle order status result for a single sales order
 */
export const OracleOrderStatusSchema = z.object({
  oracleOrderNumber: z.string(),
  headerId: z.number(),
  orderStatus: z.string(),
  creationDate: z.string(),
});

/**
 * Fetch Oracle order statuses input — accepts up to 50 sales order IDs.
 * The backend resolves which orders need an Oracle API call vs. returning
 * a cached result.
 */
export const FetchOracleOrderStatusesInputSchema = z.object({
  salesOrderIds: z.array(UUIDSchema).min(1).max(50),
});

/**
 * Fetch Oracle order statuses response — a record keyed by sales order ID.
 * Values are null when Oracle hasn't processed the order yet or the order
 * hasn't been synced.
 */
export const FetchOracleOrderStatusesResponseSchema = z.object({
  results: z.record(z.string(), OracleOrderStatusSchema.nullable()),
});

/**
 * Sales Orders RPC Contract
 */
export const salesOrdersRpcContracts = {
  get: oc
    .input(GetSalesOrderInputSchema)
    .output(GetSalesOrderResponseSchema)
    .route({
      method: "GET",
    }),
  list: oc
    .input(ListSalesOrdersInputSchema)
    .output(ListSalesOrdersResponseSchema)
    .route({
      method: "GET",
    }),
  fetchOracleOrderStatuses: oc
    .input(FetchOracleOrderStatusesInputSchema)
    .output(FetchOracleOrderStatusesResponseSchema)
    .route({
      method: "POST",
    }),
} as const;

// Type exports
export type SalesOrder = z.infer<typeof SalesOrderSchema>;
export type SalesOrderOriginMetadata = z.infer<
  typeof SalesOrderOriginMetadataSchema
>;
export type SalesOrderLineItem = z.infer<typeof SalesOrderLineItemSchema>;
export type SalesOrderCustomerSummary = z.infer<
  typeof SalesOrderCustomerSummarySchema
>;
export type SalesOrderShipmentSummary = z.infer<
  typeof SalesOrderShipmentSummarySchema
>;
export type ListSalesOrdersInput = z.infer<typeof ListSalesOrdersInputSchema>;
export type ListSalesOrdersResponse = z.infer<
  typeof ListSalesOrdersResponseSchema
>;
export type GetSalesOrderInput = z.infer<typeof GetSalesOrderInputSchema>;
export type GetSalesOrderResponse = z.infer<typeof GetSalesOrderResponseSchema>;
export type SalesOrderStatus = z.infer<typeof SalesOrderStatusSchema>;
export type OracleOrderStatus = z.infer<typeof OracleOrderStatusSchema>;
export type FetchOracleOrderStatusesInput = z.infer<
  typeof FetchOracleOrderStatusesInputSchema
>;
export type FetchOracleOrderStatusesResponse = z.infer<
  typeof FetchOracleOrderStatusesResponseSchema
>;
